/**
 * Compliance Reports Page
 * 
 * This page is ONLY visible to DigniFi organization users.
 * It displays compliance monitoring, DNC requests, and negative feedback tracking.
 */

import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Shield, AlertTriangle, PhoneOff, ThumbsDown, CheckCircle, Loader2, FileWarning, Clock } from "lucide-react";
import { useOrganizationContext } from "@/hooks/useOrganization";
import { trpc } from "@/lib/trpc";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

// Color palette
const COLORS = {
  success: "#22c55e",
  warning: "#f59e0b",
  danger: "#ef4444",
  muted: "#6b7280",
  blue: "#3b82f6",
};

export default function ComplianceReports() {
  const { organizationName, isLoading: orgLoading } = useOrganizationContext();
  
  // Fetch real data from API
  const { data: complianceSummary, isLoading: summaryLoading } = trpc.dignifi.complianceSummary.useQuery();
  const { data: complianceIssues, isLoading: issuesLoading } = trpc.dignifi.complianceIssues.useQuery();
  const { data: callSummary, isLoading: callLoading } = trpc.dignifi.callSummary.useQuery();

  const isLoading = orgLoading || summaryLoading || issuesLoading || callLoading;

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading compliance data...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Calculate compliance stats
  const dncRequests = Number(complianceSummary?.dnc_requests || 0);
  const negativeCalls = Number(complianceSummary?.negative_calls || 0);
  const positiveCalls = Number(complianceSummary?.positive_calls || 0);
  const totalCalls = Number(complianceSummary?.total_calls || 0);
  
  const dncRate = totalCalls > 0 ? ((dncRequests / totalCalls) * 100).toFixed(2) : 0;
  const negativeRate = totalCalls > 0 ? ((negativeCalls / totalCalls) * 100).toFixed(2) : 0;
  const complianceScore = totalCalls > 0 ? (100 - (Number(dncRate) + Number(negativeRate))).toFixed(1) : 100;

  // Compliance pie chart data
  const complianceData = [
    { name: 'Compliant', value: totalCalls - dncRequests - negativeCalls, color: COLORS.success },
    { name: 'DNC Requests', value: dncRequests, color: COLORS.danger },
    { name: 'Negative Feedback', value: negativeCalls, color: COLORS.warning },
  ].filter(d => d.value > 0);

  // Filter DNC requests
  const dncIssues = complianceIssues?.filter(i => ['yes', 'Yes', 'True', 'true'].includes(i.dnc)) || [];
  const negativeIssues = complianceIssues?.filter(i => i.user_sentiment === 'Negative') || [];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Compliance Reports</h1>
            <p className="text-muted-foreground mt-1">
              Regulatory Compliance & Issue Monitoring
            </p>
          </div>
        </div>

        {/* Compliance Score Banner */}
        <Card className={`bg-gradient-to-r ${Number(complianceScore) >= 95 ? 'from-green-500/20 to-green-600/10 border-green-500/30' : Number(complianceScore) >= 90 ? 'from-yellow-500/20 to-yellow-600/10 border-yellow-500/30' : 'from-red-500/20 to-red-600/10 border-red-500/30'}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-6 w-6" />
              Overall Compliance Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-8">
              <div className="text-6xl font-bold">
                {complianceScore}%
              </div>
              <div className="text-sm text-muted-foreground">
                <p>Based on {totalCalls.toLocaleString()} total calls</p>
                <p className="mt-1">
                  {Number(complianceScore) >= 95 ? 'Excellent compliance - minimal issues detected' :
                   Number(complianceScore) >= 90 ? 'Good compliance - some areas need attention' :
                   'Compliance needs improvement - review issues below'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Key Metrics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Calls</CardTitle>
              <CheckCircle className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalCalls.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                Calls analyzed
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">DNC Requests</CardTitle>
              <PhoneOff className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-500">{dncRequests}</div>
              <p className="text-xs text-muted-foreground">
                {dncRate}% of calls
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Negative Feedback</CardTitle>
              <ThumbsDown className="h-4 w-4 text-yellow-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-500">{negativeCalls}</div>
              <p className="text-xs text-muted-foreground">
                {negativeRate}% of calls
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Positive Feedback</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-500">{positiveCalls.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                {totalCalls > 0 ? ((positiveCalls / totalCalls) * 100).toFixed(1) : 0}% of calls
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Compliance Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>Compliance Distribution</CardTitle>
              <CardDescription>Breakdown of call compliance status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={complianceData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
                    >
                      {complianceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: number) => [value.toLocaleString(), "Calls"]}
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Compliance Metrics */}
          <Card>
            <CardHeader>
              <CardTitle>Compliance Metrics</CardTitle>
              <CardDescription>Key compliance indicators</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-6 w-6 text-green-500" />
                    <div>
                      <p className="font-medium">Compliant Calls</p>
                      <p className="text-sm text-muted-foreground">No issues detected</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-500">
                      {(totalCalls - dncRequests - negativeCalls).toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground">{complianceScore}%</p>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <div className="flex items-center gap-3">
                    <PhoneOff className="h-6 w-6 text-red-500" />
                    <div>
                      <p className="font-medium">DNC Requests</p>
                      <p className="text-sm text-muted-foreground">Do Not Call list additions</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-red-500">{dncRequests}</p>
                    <p className="text-sm text-muted-foreground">{dncRate}%</p>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <div className="flex items-center gap-3">
                    <ThumbsDown className="h-6 w-6 text-yellow-500" />
                    <div>
                      <p className="font-medium">Negative Sentiment</p>
                      <p className="text-sm text-muted-foreground">Customer dissatisfaction</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-yellow-500">{negativeCalls}</p>
                    <p className="text-sm text-muted-foreground">{negativeRate}%</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* DNC Requests Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PhoneOff className="h-5 w-5 text-red-500" />
              Recent DNC Requests
            </CardTitle>
            <CardDescription>Customers who requested to be added to Do Not Call list</CardDescription>
          </CardHeader>
          <CardContent>
            {dncIssues.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 font-medium">Date</th>
                      <th className="text-left py-3 px-4 font-medium">Phone (Last 4)</th>
                      <th className="text-left py-3 px-4 font-medium">Sentiment</th>
                      <th className="text-left py-3 px-4 font-medium">Summary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dncIssues.slice(0, 20).map((issue, index) => (
                      <tr key={index} className="border-b border-border/50 hover:bg-muted/50">
                        <td className="py-3 px-4 text-sm">
                          {issue.call_time ? new Date(issue.call_time).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="py-3 px-4 text-sm font-mono">
                          ***-***-{issue.consumer_phone?.slice(-4) || '****'}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                            issue.user_sentiment === 'Negative' 
                              ? 'bg-red-500/20 text-red-500' 
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            {issue.user_sentiment || 'Unknown'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground max-w-[300px] truncate">
                          {issue.detailed_call_summary || 'No summary available'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500 opacity-50" />
                <p>No DNC requests recorded. Great compliance!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Negative Feedback Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Recent Negative Feedback
            </CardTitle>
            <CardDescription>Calls with negative customer sentiment</CardDescription>
          </CardHeader>
          <CardContent>
            {negativeIssues.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 font-medium">Date</th>
                      <th className="text-left py-3 px-4 font-medium">Phone (Last 4)</th>
                      <th className="text-left py-3 px-4 font-medium">DNC Request</th>
                      <th className="text-left py-3 px-4 font-medium">Summary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {negativeIssues.slice(0, 20).map((issue, index) => (
                      <tr key={index} className="border-b border-border/50 hover:bg-muted/50">
                        <td className="py-3 px-4 text-sm">
                          {issue.call_time ? new Date(issue.call_time).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="py-3 px-4 text-sm font-mono">
                          ***-***-{issue.consumer_phone?.slice(-4) || '****'}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                            ['yes', 'Yes', 'True', 'true'].includes(issue.dnc)
                              ? 'bg-red-500/20 text-red-500' 
                              : 'bg-green-500/20 text-green-500'
                          }`}>
                            {['yes', 'Yes', 'True', 'true'].includes(issue.dnc) ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground max-w-[300px] truncate">
                          {issue.detailed_call_summary || 'No summary available'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500 opacity-50" />
                <p>No negative feedback recorded. Excellent customer satisfaction!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Compliance Guidelines */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileWarning className="h-5 w-5" />
              Compliance Guidelines
            </CardTitle>
            <CardDescription>Best practices for maintaining compliance</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium mb-2">DNC List Management</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Process DNC requests within 24 hours</li>
                  <li>• Maintain accurate DNC records</li>
                  <li>• Scrub call lists against DNC database</li>
                  <li>• Document all DNC request handling</li>
                </ul>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium mb-2">Call Quality Standards</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Identify caller and purpose clearly</li>
                  <li>• Respect customer preferences</li>
                  <li>• Follow approved call scripts</li>
                  <li>• Document all customer interactions</li>
                </ul>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium mb-2">Negative Feedback Response</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Review negative calls within 48 hours</li>
                  <li>• Identify root causes of dissatisfaction</li>
                  <li>• Implement corrective actions</li>
                  <li>• Track improvement over time</li>
                </ul>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium mb-2">Regulatory Requirements</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• TCPA compliance for all calls</li>
                  <li>• State-specific calling regulations</li>
                  <li>• Time-of-day calling restrictions</li>
                  <li>• Consent documentation requirements</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
